import React, { useCallback, useEffect, useState } from "react";
import { Search, getFloorlist } from "adminApi/HousekeepingApi/Housekeeping.js";
import Spinner from "Common/Globle/Spinner";
import SearchRoomForm from "./components/SearchRoomForm";
import fields from "../json/HousekeepingFields.json";
import useDebouncedValue from "Common/DataTable/useDebouncedValue";
import useFetchAllData from "Hooks/useFetchAllData";

/**
 * SearchRoom
 * Renders the room search/filter panel and the room card grid.
 * Fires the room status API whenever any filter value changes.
 *
 * Props:
 *  - errors       {Object}   - form validation errors (passed down to form)
 *  - values       {Object}   - shared form state
 *  - handleChange {Function} - shared onChange handler
 *  - setValues    {Function} - direct state setter (used to init limit + toggle rooms)
 */
const SearchRoom = ({ errors, values, handleChange, setValues }) => {
    const { searchdata } = fields;

    const { data: FlorListOptions = [], loading: loadingFloor } = useFetchAllData(getFloorlist);

    const [loading, setLoading] = useState(false);
    const [roomData, setRoomData] = useState([]);

    // Debounce the text search so we don't fire on every keystroke
    const debouncedSearch = useDebouncedValue(values.search || "", 500);

    // Initialise limit to 10 on first mount so the API never fires with limit=""
    useEffect(() => {
        if (!values.limit) {
            setValues((prev) => ({ ...prev, limit: 10 }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch room status whenever any filter changes
    const fetchRoomStatus = useCallback(async () => {
        // Don't fire until limit is initialised
        if (!values.limit) return;
        try {
            setLoading(true);
            const res = await Search({
                date: values.date || "",
                floor: values.floor || "",
                status: values.status || "",
                search: debouncedSearch,
                limit: values.limit,
            });
            setRoomData(res?.data || []);
        } catch (err) {
            console.error("SearchRoom fetch error:", err);
        } finally {
            setLoading(false);
        }
    }, [values.date, values.floor, values.status, values.limit, debouncedSearch]);

    useEffect(() => {
        fetchRoomStatus();
    }, [fetchRoomStatus]);

    return (
        <>
            {(loading || loadingFloor) && <Spinner isSpinner />}
            <SearchRoomForm
                fields={searchdata}
                values={values}
                handleChange={handleChange}
                loading={loading}
                FlorListOptions={FlorListOptions}
                RoomOptions={roomData}
                setValues={setValues}
                errors={errors}
            />
        </>
    );
};

export default SearchRoom;
